import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowDownRight, ArrowRight, Box, Lightbulb, Users, BarChart3 } from "lucide-react";
import { Link } from "react-router-dom";
import "./AboutScene.css";

const pillars = [
  {
    number: "01",
    title: "Practical Innovation",
    description: "We turn bold ideas into working solutions with a focus on what’s practical, scalable, and valuable in the real world.",
    icon: Lightbulb,
    tone: "cyan",
  },
  {
    number: "02",
    title: "Long-Term Partnership",
    description: "We invest in your success beyond the build, acting as a true extension of your team.",
    icon: Users,
    tone: "violet",
  },
  {
    number: "03",
    title: "Scalable Systems",
    description: "From product to infrastructure, we engineer for scale, performance, and long-term resilience.",
    icon: Box,
    tone: "magenta",
  },
  {
    number: "04",
    title: "Real Business Impact",
    description: "We measure success by the outcomes you achieve — growth, efficiency, and competitive advantage.",
    icon: BarChart3,
    tone: "aqua",
  },
];

export default function AboutScene() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => document.body.classList.toggle("home-about-active", entry.isIntersecting),
      { threshold: 0.35 },
    );

    observer.observe(section);
    return () => {
      observer.disconnect();
      document.body.classList.remove("home-about-active");
    };
  }, []);

  return (
    <section ref={sectionRef} id="about-edenox" data-testid="about-scene" className="edx-about-scene" aria-labelledby="about-scene-heading">
      <div className="edx-about-art" aria-hidden="true">
        <img src="/edenox-about-city-v1.png" alt="" width="1672" height="941" loading="lazy" />
        <svg className="edx-about-live-lights" viewBox="0 0 1672 941" preserveAspectRatio="xMidYMid slice">
          <defs>
            <filter id="about-light-soft" x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur stdDeviation="7" />
            </filter>
            <mask id="about-original-light-mask" maskUnits="userSpaceOnUse" x="0" y="0" width="1672" height="941">
              <rect width="1672" height="941" fill="black" />
              <g className="edx-about-original-routes" filter="url(#about-light-soft)">
                <path d="M770 488 C842 466 893 483 931 521 C967 557 1015 569 1092 529" pathLength="100" />
                <path d="M892 581 C970 568 1018 588 1045 631 C1072 675 1135 699 1211 699 C1287 699 1344 675 1390 637" pathLength="100" />
                <path d="M1217 456 C1290 433 1352 450 1397 480 C1452 516 1514 501 1582 465" pathLength="100" />
                <path d="M1552 698 C1465 675 1388 691 1310 739 C1242 780 1166 807 1072 785" pathLength="100" />
                <path d="M1018 615 L1018 548 L1068 519 L1068 343" pathLength="100" />
                <path d="M1266 648 L1266 549 L1322 510 L1322 332" pathLength="100" />
                <path d="M906 557 L906 492 L956 462 L956 347" pathLength="100" />
                <path d="M1414 501 L1414 434 L1478 404 L1478 322" pathLength="100" />
              </g>
            </mask>
          </defs>
          <image
            className="edx-about-original-light-texture"
            href="/edenox-about-city-v1.png"
            width="1672"
            height="941"
            preserveAspectRatio="xMidYMid slice"
            mask="url(#about-original-light-mask)"
          />
        </svg>
      </div>
      <div className="edx-about-shade" aria-hidden="true" />
      <div className="edx-about-aurora" aria-hidden="true" />
      <div className="edx-about-beacons" aria-hidden="true">
        <i /><i /><i />
      </div>

      <div className="edx-about-frame">
        <div className="edx-about-content">
          <div className="edx-about-eyebrow">
            <span>ABOUT EDENOX</span><i /><b>PEOPLE&nbsp; + &nbsp;TECHNOLOGY&nbsp; + &nbsp;REAL OUTCOMES</b>
          </div>

          <motion.h2
            id="about-scene-heading"
            className="edx-about-title"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.65 }}
          >
            <span>Built for ambitious</span>
            <span>businesses that need</span>
            <span className="edx-about-gradient">more than code.</span>
          </motion.h2>

          <p className="edx-about-intro">
            EDENOX turns ambitious ideas into resilient products, intelligent systems, and digital
            infrastructure — engineered for real-world business impact. We combine deep technical
            expertise with a partner mindset to help you move faster, scale smarter, and build what’s next.
          </p>

          <div className="edx-about-cards">
            {pillars.map(({ number, title, description, icon: Icon, tone }, index) => (
              <motion.article
                key={title}
                className={`edx-about-card edx-about-card-${tone}`}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.45 }}
                transition={{ duration: 0.45, delay: index * 0.07 }}
              >
                <Icon aria-hidden="true" />
                <div>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </div>
                <span>{number}</span>
              </motion.article>
            ))}
          </div>

          <div className="edx-about-actions">
            <Link to="/the-transmission" className="edx-about-primary">
              Learn more about us <ArrowDownRight aria-hidden="true" />
            </Link>
            <a href="#process" className="edx-about-secondary">
              See how we work <ArrowRight aria-hidden="true" />
            </a>
          </div>
        </div>

        <div className="edx-about-side-note" aria-hidden="true">
          <i />
          <span>A BRIGHTER<br />TOMORROW<br />TOGETHER</span>
        </div>
      </div>
    </section>
  );
}
